import { createSymbol, dedupeArray, isBoolean, isDefined, isNil, isNumber, isPrimitive, isString, noop, } from '@showroomjs/core';
import { createNameContext, NumberInput, useId, useQueryParams, } from '@showroomjs/ui';
import { useQuery, useQueryClient } from '@showroomjs/bundles/query';
import * as React from 'react';
import { findBestMatch } from 'string-similarity';
import { isType, useComponentMeta } from './component-props-context';
import { safeCompress, safeDecompress } from './compress';
import { parseSafely, stringifySafely } from './parse-safely';
export const NonLiteralValue = createSymbol('NonLiteral');
/**
 *
 * @param state
 * @param id
 * @param dispatch
 * @param controls the controls config to be used. This is important as some config cannot be serialized.
 * @param includes
 * @returns
 */
const stateToEditor = (state, id, dispatch, controls = state.controls, includes) => {
    const props = Object.assign({}, state.values);
    Object.entries(state.values).forEach(([prop, value]) => {
        const control = controls.find((ctrl) => ctrl.key === prop);
        if (control) {
            if (control.type === 'number') {
                props[prop] = NumberInput.getNumberValue(value);
            }
            if (control.type === 'select' &&
                state.propWithValueEncodedWithIndex.includes(prop)) {
                const currentValue = props[prop];
                if (isNumber(currentValue)) {
                    const selectedOption = control.options[currentValue];
                    if (selectedOption) {
                        props[prop] = selectedOption.value;
                    }
                }
            }
        }
    });
    return {
        props,
        controls: (includes
            ? controls.filter((ctrl) => includes.includes(ctrl.key))
            : controls).map((ctrl) => {
            const value = (function getValue() {
                const stateValue = state.values[ctrl.key];
                if (ctrl.type !== 'select') {
                    return stateValue;
                }
                if (!isNumber(stateValue) ||
                    !state.propWithValueEncodedWithIndex.includes(ctrl.key)) {
                    return stateValue;
                }
                const selectedOption = ctrl.options[stateValue];
                if (!selectedOption) {
                    return stateValue;
                }
                return selectedOption.value;
            })();
            return Object.assign(Object.assign({}, ctrl), { key: `${id}${ctrl.key}`, value, setValue: (value, index) => dispatch({
                    type: 'setValue',
                    prop: ctrl.label,
                    value,
                    index,
                }) });
        }),
    };
};
export const usePropsEditor = ({ initialProps = {}, controls: providedControls = {}, includes, } = {}) => {
    const controls = normalizeControls(providedControls);
    const componentMeta = useComponentMeta();
    const id = useId();
    const config = React.useMemo(() => computeConfig({
        componentMeta,
        initialProps,
        controls,
    }), []);
    const [_state, _dispatch] = React.useReducer(propsEditorReducer, config);
    const injectedEditor = React.useContext(PropsEditorContext);
    const state = injectedEditor[0] ? injectedEditor[0] : _state;
    const dispatch = injectedEditor[0] ? injectedEditor[1] : _dispatch;
    React.useEffect(() => {
        injectedEditor[1]({
            type: 'init',
            initialState: _state,
        });
    }, []);
    return stateToEditor(state, id, dispatch, config.controls, includes);
};
const normalizeControls = (provided) => {
    const result = {};
    Object.keys(provided).forEach((prop) => {
        const config = provided[prop];
        if (isString(config)) {
            result[prop] = {
                type: config,
            };
        }
        else if (config && config.type === 'select') {
            result[prop] = Object.assign(Object.assign({}, config), { options: Array.isArray(config.options)
                    ? config.options.map(({ type = 'literal', value, label }) => ({
                        type,
                        value,
                        label,
                    }))
                    : config.options });
        }
        else {
            result[prop] = config;
        }
    });
    return result;
};
function computeConfig({ componentMeta, initialProps, controls: controlOverrides, }) {
    const controls = [];
    const values = Object.assign({}, initialProps);
    const propWithValueEncodedWithIndex = [];
    if (!componentMeta) {
        Object.entries(controlOverrides).forEach(([key, config]) => {
            if (isDefined(config)) {
                addPropControl(config, {
                    name: key,
                    initialValue: initialProps[key],
                    required: true,
                });
            }
        });
        return {
            controls,
            values,
            propWithValueEncodedWithIndex,
        };
    }
    const propItems = Object.values(componentMeta.props);
    function addPropControl(config, prop) {
        if (config.type !== 'select') {
            controls.push(Object.assign(Object.assign({}, config), { label: prop.name, key: prop.name }));
        }
        if (config.type === 'text' && isString(prop.initialValue)) {
            values[prop.name] = prop.initialValue;
        }
        if (config.type === 'number' && isNumber(prop.initialValue)) {
            values[prop.name] = prop.initialValue;
        }
        if (config.type === 'checkbox' && isBoolean(prop.initialValue)) {
            values[prop.name] = prop.initialValue;
        }
        if (config.type === 'select') {
            if (config.options.length > 0) {
                const optionsWithType = config.options.map(({ type = 'literal', value, label }) => ({
                    value,
                    label,
                    type,
                }));
                const nullOptionIndex = optionsWithType.findIndex((opt) => opt.value === null);
                const defaultOptions = prop.required && nullOptionIndex === -1
                    ? []
                    : [
                        {
                            value: nullOptionIndex !== -1 ? null : undefined,
                            label: '(none)',
                            type: 'literal',
                        },
                    ];
                const selectControlConfig = {
                    type: 'select',
                    label: prop.name,
                    key: prop.name,
                    options: defaultOptions.concat(nullOptionIndex === -1
                        ? optionsWithType
                        : optionsWithType.filter((opt) => opt.value !== null)),
                };
                controls.push(selectControlConfig);
                if (isDefined(prop.initialValue) &&
                    config.options.some((opt) => opt.value === prop.initialValue)) {
                    values[prop.name] = selectControlConfig.options.findIndex((opt) => opt.value === prop.initialValue);
                    propWithValueEncodedWithIndex.push(prop.name);
                }
                if (prop.required && !isDefined(values[prop.name])) {
                    values[prop.name] = 0;
                    propWithValueEncodedWithIndex.push(prop.name);
                }
            }
        }
    }
    propItems.forEach((prop) => {
        const isDeprecated = prop.tags && hasProperty(prop.tags, 'deprecated');
        if (isDeprecated) {
            return;
        }
        const initialValue = (function getInitialValue() {
            if (hasProperty(initialProps, prop.name)) {
                return initialProps[prop.name];
            }
            const declaredDefaultValue = prop.defaultValue && prop.defaultValue.value;
            return !isNil(declaredDefaultValue) ? declaredDefaultValue : undefined;
        })();
        const override = controlOverrides[prop.name];
        const addOptions = Object.assign(Object.assign({}, prop), { initialValue });
        if (override) {
            if (isValidControlConfig(override)) {
                addPropControl(override, addOptions);
                return;
            }
        }
        if (isType(prop, 'string')) {
            addPropControl({ type: 'text' }, addOptions);
            return;
        }
        if (isType(prop, 'number')) {
            addPropControl({ type: 'number' }, addOptions);
            return;
        }
        if (isType(prop, 'boolean')) {
            addPropControl({ type: 'checkbox' }, addOptions);
            return;
        }
        if (isType(prop, 'File')) {
            addPropControl({ type: 'file' }, addOptions);
            return;
        }
        if (isType(prop, 'Date')) {
            addPropControl({ type: 'date' }, addOptions);
            return;
        }
        if (prop.type.name === 'enum') {
            if (prop.type.raw === 'ReactNode') {
                return;
            }
            const optionValues = prop.type.value;
            const parseOptions = Array.isArray(optionValues)
                ? optionValues
                    .map((opt) => {
                    const literalValue = parseSafely(opt.value);
                    if (isDefined(literalValue)) {
                        return {
                            value: literalValue,
                            type: 'literal',
                        };
                    }
                    const controlType = controlByTypeMap[opt.value];
                    if (controlType) {
                        return {
                            value: NonLiteralValue,
                            type: controlType,
                        };
                    }
                })
                    .filter(isDefined)
                : [];
            if (parseOptions.length > 0) {
                const options = parseOptions.map((opt) => (Object.assign(Object.assign({}, opt), { label: opt.type === 'literal' ? String(opt.value) : `(${opt.type})` })));
                addPropControl({
                    type: 'select',
                    options,
                }, addOptions);
            }
            return;
        }
    });
    const additionalControls = Object.entries(controlOverrides).filter(([key]) => !propItems.some((prop) => prop.name === key));
    additionalControls.forEach(([propName, control]) => {
        if (isDefined(control)) {
            if (isValidControlConfig(control)) {
                controls.push(Object.assign(Object.assign({}, control), { label: propName, key: propName }));
            }
        }
    });
    return {
        controls,
        values,
        propWithValueEncodedWithIndex,
    };
}
const VALID_TYPES = [
    'checkbox',
    'text',
    'object',
    'file',
    'number',
    'select',
    'date',
    'color',
];
const controlByTypeMap = {
    string: 'text',
    number: 'number',
    boolean: 'checkbox',
    object: 'object',
    File: 'file',
    Date: 'date',
};
const isValidControlConfig = (controlConfig) => {
    if (['checkbox', 'text', 'object', 'number', 'file', 'date', 'color'].includes(controlConfig.type)) {
        return true;
    }
    if (controlConfig.type === 'select') {
        if (Array.isArray(controlConfig.options) &&
            controlConfig.options.length > 0 &&
            controlConfig.options.every((option) => isString(option.label))) {
            return true;
        }
        else {
            console.warn(`options must be Array of label/value if type is 'select'.`);
            return false;
        }
    }
    const bestMatch = controlConfig.type && findBestMatch(controlConfig.type, VALID_TYPES);
    let warningMessage = `'${controlConfig.type}' is not a supported type for control.`;
    if (bestMatch.bestMatch && bestMatch.bestMatch.rating > 0.4) {
        warningMessage += `\nDo you mean to use '${bestMatch.bestMatch.target}'?`;
    }
    console.warn(warningMessage);
    return false;
};
const propsEditorReducer = (state, event) => {
    switch (event.type) {
        case 'setValue':
            return reduceNextStateForValue(state, event);
        default:
            return state;
    }
};
const hasOwnProperty = Object.prototype.hasOwnProperty;
const hasProperty = (obj, property) => hasOwnProperty.call(obj, property);
const reduceNextStateForValue = (state, event) => {
    const useValueIndex = isNumber(event.index);
    return Object.assign(Object.assign({}, state), { values: Object.assign(Object.assign({}, state.values), { [event.prop]: useValueIndex ? event.index : event.value }), propWithValueEncodedWithIndex: useValueIndex
            ? dedupeArray(state.propWithValueEncodedWithIndex.concat(event.prop))
            : state.propWithValueEncodedWithIndex.filter((p) => p !== event.prop) });
};
const propsEditorProviderReducer = (state, event) => {
    switch (event.type) {
        case 'init':
            return state || event.initialState;
        case 'setValue':
            return state && reduceNextStateForValue(state, event);
        default:
            return state;
    }
};
const usePropsEditorProviderState = (initialState) => React.useReducer(propsEditorProviderReducer, initialState);
export const PropsEditorContext = createNameContext('PropsEditorContext', [undefined, noop]);
export const PropsEditorProvider = (props) => {
    const queryClient = useQueryClient();
    const propsEditorCache = useQuery({
        enabled: false,
        queryKey: props.codeHash && getQueryKey(props.codeHash),
    });
    const [editorState, dispatch] = usePropsEditorProviderState(propsEditorCache.data);
    const [params, setParams] = useQueryParams();
    React.useEffect(() => {
        if (props.serializeToParam) {
            const serializedProps = params.get('props');
            if (serializedProps) {
                const result = safeDecompress(serializedProps, '');
                const initialState = result && parseSafely(result);
                if (initialState) {
                    dispatch({
                        type: 'init',
                        initialState,
                    });
                }
            }
        }
    }, [props.serializeToParam]);
    React.useEffect(() => {
        if (props.serializeToParam && editorState) {
            const serialized = stringifySafely(editorState);
            if (serialized) {
                setParams({
                    props: safeCompress(serialized),
                }, {
                    merge: true,
                });
            }
        }
    }, [editorState, props.serializeToParam]);
    const dispatchCb = React.useCallback((event) => {
        dispatch(event);
        if (event.type === 'init' && props.codeHash) {
            queryClient.setQueryData(getQueryKey(props.codeHash), event.initialState);
        }
    }, [props.codeHash]);
    return (React.createElement(PropsEditorContext.Provider, { value: [editorState, dispatchCb] }, props.children));
};
const getQueryKey = (codeHash) => [`propsEditor`, codeHash];
export const usePropsEditorContext = () => {
    const [state, dispatch] = React.useContext(PropsEditorContext);
    const id = useId();
    if (state) {
        return stateToEditor(state, id, dispatch);
    }
};
const serializedSymbol = createSymbol('serialized');
/**
 * serialized props editor state so it can be safely posted
 * via postMessage.
 */
export const serializePropsEditor = (state) => {
    const values = Object.assign({}, state.values);
    return Object.assign(Object.assign({}, state), { values, controls: state.controls.map((ctrl) => ctrl.type === 'select'
            ? Object.assign(Object.assign({}, ctrl), { options: ctrl.options.map((m, i) => isPrimitive(m.value)
                    ? m
                    : Object.assign(Object.assign({}, m), { value: `${serializedSymbol}${i}` })) }) : ctrl) });
};
//# sourceMappingURL=use-props-editor.js.map